import type { Descendant } from "slate";

import { specialChars } from "@smart/itops-utils-basic";

import type { EditorText } from "./elements";
import {
  EditorElement,
  isEditorElementType,
  isEditorText,
  ParagraphTag,
  TAB_PLACEHOLDER,
} from "./elements";

const headingPrefix: Record<Exclude<ParagraphTag, "p" | "span">, string> = {
  h1: "# ",
  h2: "## ",
  h3: "### ",
  h4: "#### ",
  h5: "##### ",
  h6: "###### ",
};

const escapeHtml = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

type Wrap = "b" | "i";
const wrappers: Record<Wrap, string> = {
  b: "**",
  i: "*",
};
const altWrappers: Record<Wrap, string> = {
  b: "__",
  i: "_",
};

const wrapText = (text: string, key: Wrap, alt?: boolean) => {
  const w = (alt ? altWrappers : wrappers)[key];
  return `${w}${text}${w}`;
};

const previousText = (array: Descendant[], index: number) => {
  const previous = array[index];
  return isEditorText(previous) ? previous : undefined;
};

const serializeTextNode = (
  node: EditorText,
  nodes: Descendant[],
  index: number,
  parentLength: number,
): string => {
  const leadingTabs = node.text.match(/^\t*/)?.[0] ?? "";
  const trailingTabs = node.text.match(/\t*$/)?.[0] ?? "";
  let text = node.text
    .slice(leadingTabs.length, node.text.length - trailingTabs.length)
    .trim();
  const charsToEscape = ["*", "#", "-", ">"];

  charsToEscape.forEach((char) => {
    if (text.length && text.startsWith(char))
      text = text.replace(char, `\\${char}`);
  });

  const numberedListPattern = /^(\s*)(\d+)([.)])(\s+)/gm;
  text = text.replace(numberedListPattern, "$1$2\\$3$4");

  if (text.length && node.underline) text = `<u>${escapeHtml(text)}</u>`;
  if (text.length && node.italic) text = wrapText(text, "i", node.bold);
  if (text.length && node.bold) text = wrapText(text, "b");

  if (node.text.slice(leadingTabs.length).startsWith(" ")) text = ` ${text}`;
  if (
    node.text.slice(0, node.text.length - trailingTabs.length).endsWith(" ") &&
    text !== " "
  )
    text = `${text} `;

  const prev = previousText(nodes, index - 1);
  if (
    text.startsWith("*") &&
    (prev?.italic || prev?.bold) &&
    !prev.text.endsWith(" ")
  )
    text = `${specialChars.invisible}${text}`;

  if (nodes.length === 1 && parentLength > 1 && !text) {
    text = specialChars.nbsp;
  }

  const raw = leadingTabs + text + trailingTabs;
  return raw.replace(/\t/g, TAB_PLACEHOLDER);
};

export const serialize = (
  nodes: Descendant[],
  parentLength = 0,
  delimiter = "\n\n",
): string =>
  nodes
    .map((node, index) => {
      if (isEditorElementType("paragraph")(node)) {
        const para = node as EditorElement<"paragraph">;
        const prefix =
          para.paragraphTag && para.paragraphTag in headingPrefix
            ? headingPrefix[para.paragraphTag as keyof typeof headingPrefix]
            : undefined;
        const text = serialize(node.children, nodes.length, "");
        return prefix ? prefix + text : text;
      }

      if (isEditorElementType("bulleted-list")(node)) {
        return node.children
          .map((child) =>
            isEditorElementType("list-item")(child)
              ? `- ${serialize(child.children, node.children.length, "")}`
              : "",
          )
          .filter(Boolean)
          .join("\n");
      }

      if (isEditorElementType("numbered-list")(node)) {
        return node.children
          .map((child, i) =>
            isEditorElementType("list-item")(child)
              ? `${i + 1}. ${serialize(child.children, node.children.length, "")}`
              : "",
          )
          .filter(Boolean)
          .join("\n");
      }

      if (isEditorElementType("blockquote")(node)) {
        const inner = serialize(node.children, nodes.length, delimiter);
        if (!inner.trim()) return "";
        return inner
          .split("\n")
          .map((line) => `> ${line}`)
          .join("\n");
      }

      if (isEditorElementType("list-item")(node)) {
        return serialize(node.children, nodes.length, "");
      }

      if (isEditorElementType("link")(node)) {
        const linkText = serialize(node.children, nodes.length, "");
        if (linkText.trim().length > 0) return `[${linkText}](${node.url})`;
        // keep textless links that still have a URL so save on blur does not drop it
        return node.url ? `[](${node.url})` : "";
      }

      if (isEditorElementType("placeholder")(node)) {
        const { name, isLink, displayText, casing } = node;
        const casingPrefix = casing === "lowercase" ? "_" : "";
        return name
          ? `{${casingPrefix}${name}${displayText ? `|${isLink ? ">" : ""}${displayText}` : ""}}`
          : "";
      }

      if (isEditorText(node)) {
        return serializeTextNode(node, nodes, index, parentLength);
      }

      return "";
    })
    .join(delimiter);
